import type { ProfitAgentDrilldown } from '../types/profitabilityAgent';

export const PROFIT_DRILLDOWN_STORAGE_KEY = 'zecure_profit_drilldown_v1';

export type CustomerProfitSortField =
  | 'deliveryDate'
  | 'customer'
  | 'orderCount'
  | 'totalRevenue'
  | 'totalAllocatedCost'
  | 'totalAllocatedMiles'
  | 'costPerMileGbp'
  | 'totalProfit'
  | 'profitMarginPct';

export type StoredProfitDrilldown = {
  type: 'vehicle' | 'customer';
  id: string;
  date_from: string;
  date_to: string;
  /** Scroll to the Customer profitability table (Data view). */
  focus_section?: 'customer';
  customer_sort_field?: CustomerProfitSortField;
  customer_sort_asc?: boolean;
};

export function isCustomerProfitabilityQuestion(question: string): boolean {
  const q = String(question || '').trim();
  if (!q) return false;
  return (
    /\b(customer|customers|account|accounts).*\b(profit|profitable|unprofitable|margin|loss[-\s]making)\b/i.test(q) ||
    /\b(profit|profitable|unprofitable|margin|loss[-\s]making).*\b(customer|customers|account|accounts)\b/i.test(q) ||
    /\b(least|lowest|worst|bottom|top|most|highest)\b.*\b(customer|customers)\b/i.test(q)
  );
}

function inferCustomerProfitSortAsc(question: string): boolean | undefined {
  const q = String(question || '').trim();
  if (!q) return undefined;
  if (/\b(least|lowest|worst|bottom|unprofitable|loss[-\s]making)\b/i.test(q)) return true;
  if (/\b(most|highest|best)\b.*\b(profitable|profit|margin)\b/i.test(q)) return false;
  if (/\b(top)\b.*\b(profitable|profit)\b/i.test(q)) return false;
  return undefined;
}

export function buildStoredProfitDrilldown(
  d: ProfitAgentDrilldown,
  window: { date_from: string; date_to: string },
  opts?: { question?: string; tools_used?: string[] },
): StoredProfitDrilldown {
  const stored: StoredProfitDrilldown = {
    type: d.type === 'customer' ? 'customer' : 'vehicle',
    id: d.id || '',
    date_from: window.date_from,
    date_to: window.date_to,
  };

  const customerContext =
    d.type === 'customer' ||
    isCustomerProfitabilityQuestion(opts?.question ?? '') ||
    (opts?.tools_used ?? []).includes('get_customer_analysis');

  if (customerContext) {
    stored.focus_section = 'customer';
    stored.customer_sort_field = 'totalProfit';
    const inferredAsc = inferCustomerProfitSortAsc(opts?.question ?? '');
    stored.customer_sort_asc = inferredAsc ?? true;
  }

  return stored;
}

export function readStoredProfitDrilldown(): StoredProfitDrilldown | null {
  try {
    const raw = sessionStorage.getItem(PROFIT_DRILLDOWN_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PROFIT_DRILLDOWN_STORAGE_KEY);
    const parsed = JSON.parse(raw) as StoredProfitDrilldown;
    if (!parsed?.date_from || !parsed?.date_to) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeStoredProfitDrilldown(stored: StoredProfitDrilldown): void {
  try {
    sessionStorage.setItem(PROFIT_DRILLDOWN_STORAGE_KEY, JSON.stringify(stored));
    window.dispatchEvent(new CustomEvent('zecure:profit-drilldown'));
  } catch {
    // ignore quota / private mode
  }
}
